import { FC, memo, useCallback } from 'react'

import { ReportFile } from 'commons/types/DTO/bank'
import { UploadedFile } from 'commons/types/DTO/bank/quarterlyReport/uploadedFile'
import { TableDataQa } from 'commons/types/enums'
import { Layer, PageSectionSecondary, Table } from 'ui/components'

import { UploadedFilesTableWebPageProps } from './UploadedFilesTableWebPageProps'
import { columns } from './columns'
import { UploadButton } from './components/UploadButton/UploadButton'
import { useDeleteReportFile, useUploadReportFileCreate } from './hooks'

export const UploadedFilesTableWebPage: FC<UploadedFilesTableWebPageProps> = memo((props) => {
  const {
    uploadedFiles,
    uploadedFilesTableState,
    uploadedFilesTotalCount,
    changePagination,
    reportMonthlyId,
    reportQuarterlyId,
    loading = false,
    invalidate
  } = props
  const { createReportFile } = useUploadReportFileCreate(invalidate, reportQuarterlyId)
  const { deleteReportFile } = useDeleteReportFile(invalidate, reportQuarterlyId)

  const onFilesUpload = useCallback(
    async (files: File[]) => {
      const filePromises: Promise<ReportFile>[] = []
      files.forEach((file) => {
        filePromises.push(
          createReportFile({
            file,
            reportMonthlyId: reportMonthlyId ?? 0,
            reportQuarterlyId: reportQuarterlyId ?? 0
          })
        )
      })
      await Promise.all(filePromises)
    },
    [createReportFile, reportMonthlyId, reportQuarterlyId]
  )

  const onFileDelete = useCallback(
    (id: number) => {
      deleteReportFile({ id })
    },
    [deleteReportFile]
  )

  return (
    <PageSectionSecondary
      face="outlined"
      title="Uploaded Files"
      anchor="uploaded-files"
      actions={<UploadButton onUpload={onFilesUpload} />}
    >
      <Layer rounded shadowed stretch>
        <Table<UploadedFile>
          loading={loading}
          dataQa={TableDataQa.BP_QUARTERLY_ANALYTICS_UPLOADED_FILES}
          columns={columns(onFileDelete)}
          dataSource={uploadedFiles.map((el) => ({
            item: el
          }))}
          pagination={{
            page: uploadedFilesTableState.page,
            size: uploadedFilesTableState.size,
            onChange: changePagination,
            total: uploadedFilesTotalCount
          }}
        />
      </Layer>
    </PageSectionSecondary>
  )
})
